import type { AppError } from "@/shared/api";

/** Контекст виклику — визначає очікуваність помилки й канал показу (ADR-0010 D4/D5). */
export type ServiceErrorContext = "create" | "rename" | "delete";

/** Канал показу: інлайн під рядком vs глобальний toast. */
export type ServiceErrorSurface = "inline" | "toast";

/**
 * i18n-ключі помилок послуг (типобезпечний `t()`). Доменні `services.errors.*`
 * (ADR-0010 Follow-up) поверх generic `errors.*`.
 */
export type ServiceErrorKey =
  | "services.errors.invalidName"
  | "services.errors.notFound"
  | "services.errors.inUse"
  | "errors.forbidden"
  | "errors.rateLimited"
  | "errors.server"
  | "errors.network"
  | "errors.unknown";

export interface MappedServiceError {
  surface: ServiceErrorSurface;
  i18nKey: ServiceErrorKey;
  /** Чи варто прибрати рядок (stale — послугу вже видалено деінде). */
  removeRow: boolean;
  /** Варіант toast, коли `surface==='toast'`. */
  toastVariant: "destructive" | "info";
}

/**
 * Тонкий адаптер `AppError → {surface, i18nKey}` (ADR-0010 D9, патерн `mapLoginError`).
 * Гілка по `kind` (interceptor гарантує `AppError`), плюс service-специфіка:
 *
 * - `validation` + `invalid_service_name` (422) → **інлайн** під рядком (очікувано,
 *   породжено вводом). Інші `validation` (400 `invalid_body`/`invalid_id`) →
 *   неочікувано (баг/контракт) → toast generic (spec §7).
 * - `notFound` (404 `service_not_found`) → прибрати рядок + toast(info): stale.
 * - `conflict` (409 `service_in_use`) → **інлайн block** у delete-confirm.
 * - решта (`forbidden`/`rateLimited`/`server`/`network`/`unknown`/`auth`) → toast(destructive)
 *   generic. 401 (`auth`) сюди майже не доходить — глобальний interceptor редіректить (ADR-0006 D4).
 */
export function mapServiceError(
  e: AppError,
  ctx: ServiceErrorContext,
): MappedServiceError {
  switch (e.kind) {
    case "validation":
      if (e.code === "invalid_service_name") {
        return {
          surface: "inline",
          i18nKey: "services.errors.invalidName",
          removeRow: false,
          toastVariant: "destructive",
        };
      }
      // 400 invalid_body / invalid_id — неочікувано (контракт/баг).
      return {
        surface: "toast",
        i18nKey: "errors.unknown",
        removeRow: false,
        toastVariant: "destructive",
      };
    case "notFound":
      return {
        surface: "toast",
        i18nKey: "services.errors.notFound",
        removeRow: true,
        toastVariant: "info",
      };
    case "conflict":
      // 409 на delete = блок «спершу зніміть з майстрів» (інлайн). Поза delete —
      // не очікується; показуємо тим самим доменним текстом.
      return {
        surface: ctx === "delete" ? "inline" : "toast",
        i18nKey: "services.errors.inUse",
        removeRow: false,
        toastVariant: "destructive",
      };
    case "forbidden":
      return { surface: "toast", i18nKey: "errors.forbidden", removeRow: false, toastVariant: "destructive" };
    case "rateLimited":
      return { surface: "toast", i18nKey: "errors.rateLimited", removeRow: false, toastVariant: "destructive" };
    case "network":
      return { surface: "toast", i18nKey: "errors.network", removeRow: false, toastVariant: "destructive" };
    case "server":
      return { surface: "toast", i18nKey: "errors.server", removeRow: false, toastVariant: "destructive" };
    default:
      return { surface: "toast", i18nKey: "errors.unknown", removeRow: false, toastVariant: "destructive" };
  }
}
